import { PaginationState } from '@tanstack/react-table';
import { parseContractComplianceError, RuleExceptionRequests } from 'api/requests/contract-compliance-requests';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONTRACT_COMPLIANCE__LOAD_ERROR } from 'translations/constants';
import { ContractComplianceRule, IRuleException } from 'types/contract-compliance/IContractCompliance';
import { INITIAL_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from '../constants';
import { getContractComplianceErrorMessage } from '../utils';

/** Server-paged, searchable exception list of one rule (one instance per rule card). */
export const useRuleExceptions = (rule: ContractComplianceRule, requests: RuleExceptionRequests) => {
  const [t] = useTranslation();
  const [rows, setRows] = useState<IRuleException[]>([]);
  const [numberOfResults, setNumberOfResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // TanStack's pageIndex is 0-based; the API's is 1-based.
  const [pagination, setPaginationState] = useState<PaginationState>({ pageIndex: 0, pageSize: INITIAL_PAGE_SIZE });
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState<string | null>(null);
  const latestRequest = useRef(0);

  const fetchPage = useCallback(
    async (page: PaginationState, search: string) => {
      latestRequest.current += 1;
      const requestId = latestRequest.current;
      try {
        const result = await requests.getPage({
          rule,
          search: search.trim(),
          pageIndex: page.pageIndex + 1,
          pageSize: page.pageSize,
        });
        if (requestId !== latestRequest.current) return;
        // A removal can empty the last page: step back to the new last page instead of showing an empty one.
        if (result.items.length === 0 && page.pageIndex > 0 && page.pageIndex > result.totalPages - 1) {
          setPaginationState({ ...page, pageIndex: Math.max(result.totalPages - 1, 0) });
          return;
        }
        setRows(result.items);
        setNumberOfResults(result.numberOfResults);
        setTotalPages(result.totalPages);
        setLoadErrorMessage(null);
        setHasLoaded(true);
      } catch (error) {
        if (requestId !== latestRequest.current) return;
        setLoadErrorMessage(
          getContractComplianceErrorMessage(t, parseContractComplianceError(error), CONTRACT_COMPLIANCE__LOAD_ERROR),
        );
      }
    },
    [rule, requests, t],
  );

  useEffect(() => {
    fetchPage(pagination, debouncedSearch);
  }, [fetchPage, pagination, debouncedSearch]);

  const onSearch = useCallback((term: string) => {
    setSearchTerm(term);
    setPaginationState(previous => (previous.pageIndex === 0 ? previous : { ...previous, pageIndex: 0 }));
  }, []);

  /** A page-size change goes back to the first page. */
  const setPagination = useCallback((next: PaginationState) => {
    setPaginationState(previous =>
      next.pageSize !== previous.pageSize ? { pageIndex: 0, pageSize: next.pageSize } : next,
    );
  }, []);

  const reload = useCallback(() => fetchPage(pagination, debouncedSearch), [fetchPage, pagination, debouncedSearch]);

  return {
    rows,
    numberOfResults,
    totalPages,
    pagination,
    setPagination,
    searchTerm,
    debouncedSearch,
    onSearch,
    hasLoaded,
    loadErrorMessage,
    reload,
  };
};

export type UseRuleExceptionsResult = ReturnType<typeof useRuleExceptions>;
